import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import { simpleDateAtTime } from 'lib/date';
import { FacilityNetworkSummary, PayerNetworkSummary } from 'models/GroupPayerNetworkStatus';
import { PERMISSIONS } from 'models/Profile';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

import type { RemoveAllMappingsTarget } from './RemoveAllMappingsModal';

export const BASE_PATH = '/configuration/facility-network-status';

export const countOf = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const facilityColumn = createColumnHelper<FacilityNetworkSummary>();
const payerColumn = createColumnHelper<PayerNetworkSummary>();

function LastUpdated({ at, by }: { at: string | null; by: string | null }) {
  if (!at) return <BodySmall>&mdash;</BodySmall>;

  return <BodySmall>{by ? `${simpleDateAtTime(at)} by ${by}` : simpleDateAtTime(at)}</BodySmall>;
}

export function facilityColumns(onRemove: OnRemove): ColumnDef<FacilityNetworkSummary, any>[] {
  return [
    facilityColumn.accessor('name', {
      id: 'name',
      header: 'Facility',
      cell: (info) => (
        <NameLink to={`${BASE_PATH}/facilities/${info.row.original.id}`} state={{ name: info.getValue() }}>
          {info.getValue()}
        </NameLink>
      ),
      size: 320,
    }),
    facilityColumn.accessor('payerCount', {
      id: 'payerCount',
      header: 'Payers',
      cell: (info) => <BodySmall>{countOf(info.getValue(), 'payer', 'payers')}</BodySmall>,
      size: 160,
    }),
    facilityColumn.accessor('updatedAt', {
      id: 'updatedAt',
      header: 'Last Updated',
      sortDescFirst: true,
      cell: ({ row: { original } }) => <LastUpdated at={original.updatedAt} by={original.updatedByName} />,
      size: 360,
    }),
    facilityColumn.display({
      id: 'menu',
      cell: ({ row: { original } }) => (
        <RemoveAllMappingsMenu
          target={{ kind: 'facility', id: original.id, name: original.name, count: original.payerCount }}
          onRemove={onRemove}
        />
      ),
      size: 40,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];
}

export function payerColumns(onRemove: OnRemove): ColumnDef<PayerNetworkSummary, any>[] {
  return [
    payerColumn.accessor('name', {
      id: 'name',
      header: 'Payer',
      cell: (info) => (
        <NameLink to={`${BASE_PATH}/payers/${info.row.original.id}`} state={{ name: info.getValue() }}>
          {info.getValue()}
        </NameLink>
      ),
      size: 320,
    }),
    payerColumn.accessor('groupCount', {
      id: 'groupCount',
      header: 'Facilities',
      cell: (info) => <BodySmall>{countOf(info.getValue(), 'facility', 'facilities')}</BodySmall>,
      size: 160,
    }),
    payerColumn.accessor('updatedAt', {
      id: 'updatedAt',
      header: 'Last Updated',
      sortDescFirst: true,
      cell: ({ row: { original } }) => <LastUpdated at={original.updatedAt} by={original.updatedByName} />,
      size: 360,
    }),
    payerColumn.display({
      id: 'menu',
      cell: ({ row: { original } }) => (
        <RemoveAllMappingsMenu
          target={{ kind: 'payer', id: original.id, name: original.name, count: original.groupCount }}
          onRemove={onRemove}
        />
      ),
      size: 40,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];
}

type OnRemove = (target: RemoveAllMappingsTarget) => void;

function RemoveAllMappingsMenu({ target, onRemove }: { target: RemoveAllMappingsTarget; onRemove: OnRemove }) {
  const { profile } = useProfile();

  if (!profile.has(PERMISSIONS.groupPayerNetworkStatusEdit) || target.count === 0) return null;

  return (
    <Menu $size='small'>
      <Menu.Trigger>
        <KebabButton type='button' aria-label='Row actions'>
          <ThreeDotMenu />
        </KebabButton>
      </Menu.Trigger>
      <Menu.Content position='right'>
        <RemoveItem onClick={() => onRemove(target)}>
          <TrashIcon width={14} height={14} color={colors.red100} />
          Remove all mappings
        </RemoveItem>
      </Menu.Content>
    </Menu>
  );
}

const NameLink = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.blue100};
`;

export const KebabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const RemoveItem = styled(Menu.Item)`
  color: ${({ theme }) => theme.colors.red100};
`;
